import React, { useState } from 'react';
import { Card, Typography, Tabs, List, Tag, Skeleton, Empty } from 'antd';
import {
  DollarOutlined,
  UserOutlined,
  FileTextOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const STAGE_COLORS = {
  QUALIFICATION: 'blue',
  NEEDS_ANALYSIS: 'purple',
  VALUE_PROPOSITION: 'cyan',
  IDENTIFY_DECISION_MAKERS: 'gold',
  PROPOSAL_PRICE_QUOTE: 'orange',
  NEGOTIATION_REVIEW: 'magenta',
  CLOSED_WON: 'green',
  CLOSED_LOST: 'red',
  CLOSED_LOST_TO_COMPETITION: 'red',
};

const INVOICE_STATUS_COLORS = {
  PAID: 'green',
  SENT: 'blue',
  PARTIAL: 'orange',
  DRAFT: 'default',
};

const RecentActivities = ({ deals, leads, invoices, loading, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('deals');

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value?.toLocaleString('en-IN')}`;
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).fromNow();
  };

  const renderDealItem = (deal) => (
    <List.Item
      className="cursor-pointer"
      onClick={() => onNavigate(`/deals/${deal.id}`)}
      style={{ padding: '8px 0' }}
    >
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: '13px' }} ellipsis>
              <DollarOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
              {deal.deal_name || deal.name || 'Unnamed Deal'}
            </Text>
            <div style={{ marginTop: '2px' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {deal.account_name || deal.account || 'No Account'}
              </Text>
            </div>
          </div>
          <div style={{ textAlign: 'right', marginLeft: '8px' }}>
            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>
              {formatCurrency(deal.amount)}
            </Text>
            <div>
              <Tag
                color={STAGE_COLORS[deal.stage] || 'default'}
                style={{ fontSize: '10px', marginRight: 0 }}
              >
                {deal.stage_label || deal.stage || 'Unknown'}
              </Tag>
            </div>
          </div>
        </div>
        <div style={{ marginTop: '4px' }}>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {getTimeAgo(deal.created_at || deal.modified_at)}
          </Text>
        </div>
      </div>
    </List.Item>
  );

  const renderLeadItem = (lead) => (
    <List.Item
      className="cursor-pointer"
      onClick={() => onNavigate(`/leads/${lead.id}`)}
      style={{ padding: '8px 0' }}
    >
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: '13px' }} ellipsis>
              <UserOutlined style={{ marginRight: '6px', color: '#722ed1' }} />
              {lead.first_name} {lead.last_name}
            </Text>
            <div style={{ marginTop: '2px' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {lead.company || lead.email || 'No Company'}
              </Text>
            </div>
          </div>
          <div style={{ textAlign: 'right', marginLeft: '8px' }}>
            <Tag color="blue" style={{ fontSize: '10px', marginRight: 0 }}>
              {lead.source || 'Unknown'}
            </Tag>
            {lead.is_converted && (
              <Tag color="green" style={{ fontSize: '10px', marginRight: 0, marginTop: '2px' }}>
                Converted
              </Tag>
            )}
          </div>
        </div>
        <div style={{ marginTop: '4px' }}>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {getTimeAgo(lead.created_at)}
          </Text>
        </div>
      </div>
    </List.Item>
  );

  const renderInvoiceItem = (invoice) => (
    <List.Item
      className="cursor-pointer"
      onClick={() => onNavigate(`/invoices/${invoice.id}`)}
      style={{ padding: '8px 0' }}
    >
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: '13px' }} ellipsis>
              <FileTextOutlined style={{ marginRight: '6px', color: '#fa8c16' }} />
              {invoice.invoice_number || `INV-${invoice.id}`}
            </Text>
            <div style={{ marginTop: '2px' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {invoice.account_name || invoice.customer || 'No Customer'}
              </Text>
            </div>
          </div>
          <div style={{ textAlign: 'right', marginLeft: '8px' }}>
            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>
              {formatCurrency(invoice.total || invoice.amount)}
            </Text>
            <div>
              <Tag
                color={INVOICE_STATUS_COLORS[invoice.status] || 'default'}
                style={{ fontSize: '10px', marginRight: 0 }}
              >
                {invoice.status || 'Draft'}
              </Tag>
            </div>
          </div>
        </div>
        <div style={{ marginTop: '4px' }}>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {getTimeAgo(invoice.created_at || invoice.invoice_date)}
          </Text>
        </div>
      </div>
    </List.Item>
  );

  const tabItems = [
    {
      key: 'deals',
      label: (
        <span>
          <DollarOutlined />
          Deals
        </span>
      ),
      children: loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : deals?.length > 0 ? (
        <List
          dataSource={deals}
          renderItem={renderDealItem}
          size="small"
          split={true}
        />
      ) : (
        <Empty description="No recent deals" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ),
    },
    {
      key: 'leads',
      label: (
        <span>
          <UserOutlined />
          Leads
        </span>
      ),
      children: loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : leads?.length > 0 ? (
        <List
          dataSource={leads}
          renderItem={renderLeadItem}
          size="small"
          split={true}
        />
      ) : (
        <Empty description="No recent leads" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ),
    },
    {
      key: 'invoices',
      label: (
        <span>
          <FileTextOutlined />
          Invoices
        </span>
      ),
      children: loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : invoices?.length > 0 ? (
        <List
          dataSource={invoices}
          renderItem={renderInvoiceItem}
          size="small"
          split={true}
        />
      ) : (
        <Empty description="No recent invoices" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ),
    },
  ];

  return (
    <Card
      size="small"
      title={<Text strong>Recent Activities</Text>}
      bodyStyle={{ padding: '0 12px 12px' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="small"
      />
    </Card>
  );
};

export default RecentActivities;
